'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  formatPackageDateString,
  isPastPackageDate,
  isValidPackageDate,
  parsePackageDateString,
  startOfLocalDay,
  getPackageDatePricingStatus,
} from '@/utils/packageAvailabilityCalendar';

interface DropdownDatePickerProps {
  availableDates: string[];
  value?: Date;
  onDateSelect: (date: Date, status: 'special' | 'weekend' | 'allowed') => void;
  weekendDays?: string[];
  specialDates?: string[];
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Set when rendered inside a bottom sheet so the mobile picker stays above it */
  inBottomSheet?: boolean;
}

export function DropdownDatePicker({
  availableDates,
  value,
  onDateSelect,
  weekendDays = [],
  specialDates = [],
  className,
  disabled = false,
  placeholder = 'Select a date',
  inBottomSheet = false,
}: DropdownDatePickerProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const today = React.useMemo(() => startOfLocalDay(), []);

  const sortedDates = React.useMemo(
    () =>
      [...availableDates]
        .map(dateStr => ({
          dateStr,
          date: parsePackageDateString(dateStr),
        }))
        .filter(({ date }) => isValidPackageDate(date))
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
    [availableDates]
  );

  const isValidValue = value != null && isValidPackageDate(value);
  const selectedValue = isValidValue
    ? formatPackageDateString(value)
    : undefined;
  const displayLabel = isValidValue
    ? format(value, 'EEE dd MMM, yyyy')
    : placeholder;

  const handleDateSelect = (dateStr: string) => {
    const date = parsePackageDateString(dateStr);
    if (isPastPackageDate(date, today)) return;
    onDateSelect(
      date,
      getPackageDatePricingStatus(date, weekendDays, specialDates)
    );
    setMobileOpen(false);
  };

  const mobileOverlayZ = inBottomSheet ? 'z-[100000]' : 'z-[99999]';

  return (
    <div className={cn('grid gap-2', className)}>
      {/* Desktop */}
      <div className='hidden md:block'>
        <Select
          value={selectedValue}
          onValueChange={handleDateSelect}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              'w-full rounded-sm border-secondary bg-white text-sm shadow-xs'
            )}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className='max-h-60 bg-white z-[10001]'>
            {sortedDates.map(({ dateStr, date }) => {
              const isPast = isPastPackageDate(date, today);
              return (
                <SelectItem
                  key={dateStr}
                  value={dateStr}
                  disabled={isPast}
                  className={cn(isPast && 'text-muted-foreground')}
                >
                  {format(date, 'EEE dd MMM, yyyy')}
                  {isPast ? ' (past)' : ''}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Mobile */}
      <div className='md:hidden'>
        <Button
          type='button'
          variant='outline'
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal rounded-sm text-sm bg-white',
            !isValidValue && 'text-muted-foreground'
          )}
          onClick={() => !disabled && setMobileOpen(true)}
        >
          <CalendarIcon className='mr-2 h-4 w-4 flex-shrink-0' />
          <span className='truncate flex-1'>{displayLabel}</span>
          <ChevronDownIcon className='h-4 w-4 flex-shrink-0 opacity-50' />
        </Button>

        {mobileOpen &&
          typeof document !== 'undefined' &&
          createPortal(
            <div className={cn('fixed inset-0', mobileOverlayZ)}>
              <div
                className='absolute inset-0 bg-black/50'
                onClick={() => setMobileOpen(false)}
                aria-hidden
              />

              <div className='absolute bottom-0 left-0 right-0 z-[100001] bg-white rounded-t-2xl shadow-lg'>
                <div className='flex justify-center pt-3 pb-2'>
                  <div className='w-12 h-1 bg-gray-300 rounded-full' />
                </div>

                <div className='px-4 pb-3 border-b'>
                  <h3 className='text-lg font-semibold text-center'>
                    Select Date
                  </h3>
                </div>

                <div className='px-4 py-2 max-h-[60vh] overflow-y-auto'>
                  {sortedDates.map(({ dateStr, date }) => {
                    const isPast = isPastPackageDate(date, today);
                    const isSelected =
                      isValidValue &&
                      formatPackageDateString(value) === dateStr;

                    return (
                      <button
                        key={dateStr}
                        type='button'
                        disabled={isPast}
                        onClick={() => handleDateSelect(dateStr)}
                        className={cn(
                          'flex w-full items-center rounded-sm px-3 py-3 text-left text-sm',
                          isPast
                            ? 'cursor-not-allowed text-muted-foreground opacity-50'
                            : 'hover:bg-accent hover:text-accent-foreground',
                          isSelected &&
                            'bg-accent text-accent-foreground font-medium'
                        )}
                      >
                        {format(date, 'EEE dd MMM, yyyy')}
                        {isPast ? ' (past)' : ''}
                      </button>
                    );
                  })}
                </div>

                <div className='px-4 pb-6 border-t pt-4'>
                  <Button
                    variant='outline'
                    className='w-full'
                    onClick={() => setMobileOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}
